/**
 * IBAS About data — single source of truth for the /about page
 * (Vision & Mission · Management & Advisory Board · International Faculty ·
 * Accreditations & Memberships · EduQua ★ · Quality Assurance ★ ·
 * Social Responsibility ★ · Code of Conduct ★ · Contact Us ★).
 *
 * SOURCING (ibas-brand skill, HARD rule): every institutional fact here comes
 * ONLY from the branding folder, https://ibas.edu.eu, or the user — nothing is
 * invented. Where a fact is not verified it is LEFT OUT, never guessed:
 *   • No formal Mission statement exists on the live site — NOT fabricated. The
 *     "mission" block uses the branding-approved tagline + promise instead.
 *   • Faculty head-count returned inconsistently — NO count is published; only
 *     the verified roster and the countries represented.
 *   • "PRS" could not be confirmed as a distinct mark — omitted.
 *
 * Sources:
 *   • Vision / values:        https://ibas.edu.eu/about/vision-mission/
 *   • Management / board:     https://ibas.edu.eu/about/management-international-advisory-board/
 *   • Faculty:                https://ibas.edu.eu/about/international-faculty/
 *   • Accreditations:         https://ibas.edu.eu/about/accreditations-and-memberships/
 *   • EduQua:                 https://ibas.edu.eu/eduqua-certification/
 *   • Quality (PDCA ★):       branding / "IBAS Website Requirements from
 *                             Accreditations.PDF" Appendix A (VERBATIM) +
 *                             https://ibas.edu.eu/about/quality-assurance/
 *   • Social Responsibility ★: Appendix E (VERBATIM)
 *   • Code of Conduct ★:      Appendix D (VERBATIM)
 *   • Contact ★:              Appendix B (VERBATIM) + https://ibas.edu.eu/contact-us/
 *
 * ★ = accreditation-mandated verbatim copy: layout may adapt, WORDING MAY NOT.
 * Brand assets (tagline, promise, FLEXI·EDU·CARE) are approved messaging from
 * the branding guidelines and used verbatim. EduQua carries NO logo here (its
 * mark is reserved for its own dedicated page) and is never shown beside the
 * other accreditation marks.
 */

import {
  Accessibility,
  Award,
  BadgeCheck,
  Building2,
  BookOpen,
  Briefcase,
  Camera,
  ClipboardCheck,
  Compass,
  Gavel,
  GraduationCap,
  Globe,
  Handshake,
  Landmark,
  Leaf,
  Lightbulb,
  Mail,
  MapPin,
  Phone,
  PlayCircle,
  RefreshCw,
  Scale,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
 * 1 · VISION & MISSION   (source: /about/vision-mission/ + branding messaging)
 * ═══════════════════════════════════════════════════════════════════════════ */

/** The institution's vision statement — reproduced verbatim from the live site. */
export const VISION_STATEMENT =
  'As a global educational institution in Business, Hospitality, and Management, IBAS draws strength from its agile approach and versatile educational, business and cultural backgrounds to deliver a learning experience that empowers our diverse students to develop the competencies needed for a successful global career in an ever-changing world.';

/** Approved institutional tagline (brand guidelines) — the foundational commitment. */
export const FOUNDATIONAL_COMMITMENT = 'Quality Education. Affordable and Accessible for All.';

/** Approved brand promise (brand guidelines). */
export const BRAND_PROMISE = 'Study flexibly. Develop practical capability. Progress globally.';

/**
 * The six core values named on the vision page. Names are reproduced as listed;
 * per the sourcing rule no descriptive copy is invented for them.
 */
export const CORE_VALUES: readonly string[] = [
  'Accessible',
  'Agile',
  'Authentic',
  'Applied',
  'Adaptive',
  'Augmenting',
];

/** The FLEXI · EDU · CARE delivery framework (brand guidelines). */
export interface Pillar {
  key: string;
  title: string;
  description: string;
  Icon: LucideIcon;
}

export const DELIVERY_FRAMEWORK: readonly Pillar[] = [
  { key: 'FLEXI', title: 'FLEXI', description: 'Flexible design.', Icon: Compass },
  { key: 'EDU', title: 'EDU', description: 'Applied depth.', Icon: BookOpen },
  { key: 'CARE', title: 'CARE', description: 'Support.', Icon: Handshake },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 2 · MANAGEMENT & INTERNATIONAL ADVISORY BOARD
 *     (source: /about/management-international-advisory-board/)
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface Leader {
  name: string;
  title: string;
  /** Short bio where the source provides one; omitted otherwise (never invented). */
  bio?: string;
}

export const MANAGEMENT_TEAM: readonly Leader[] = [
  {
    name: 'Dr. Martin Nielsen',
    title: 'President & Founder',
    bio: 'Over 30 years of entrepreneurial and business ownership experience, combined with more than 20 years of teaching at the university level.',
  },
  {
    name: 'Julie Nielsen',
    title: 'Vice President & Head of Marketing',
    bio: 'Leads brand and strategic development since 2021, with a focus on growth, market positioning, and international partnerships.',
  },
  {
    name: 'Beata Hubnerova',
    title: 'On-Site Operations Manager',
    bio: 'Over 25 years of experience working in international business environments, with a focus on HR, expat support, and visa processes.',
  },
  { name: 'Anna Phan', title: 'Director of Administration & Finance' },
  {
    name: 'Dr. Natalia Blagoeva',
    title: 'Dean, School of Business & Quality Assurance Director',
    bio: 'Teaching business subjects at IBAS and the International Business School of Scandinavia since 2019, with over two decades of C-level and entrepreneurial experience.',
  },
  { name: 'Joseph Kiang', title: 'Registrar' },
  {
    name: 'Michael Eugster',
    title: 'Hospitality Programmes & Faculty',
    bio: 'Over 20 years of international experience in the hospitality and service industry.',
  },
  { name: 'Hemarubiny Doraisamy', title: 'Director of International Business Development' },
  { name: 'Jean-Claude Zollinger', title: 'Andragogical Lead Teacher' },
];

/**
 * International Advisory Board members. The source lists names only; no titles or
 * bios are published there, so none are invented here.
 */
export const ADVISORY_BOARD: readonly string[] = [
  'Roy Prasad',
  'Boone Ongchoco',
  'William Nguyen',
  'Hassan Al Nouri',
  'Mae Ho-Seok Khen',
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 3 · INTERNATIONAL FACULTY   (source: /about/international-faculty/)
 *     No head-count is published (the source returned inconsistent counts).
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface FacultyMember {
  name: string;
  /** Credential snippet as summarised on the source page. */
  detail: string;
}

export const FACULTY: readonly FacultyMember[] = [
  { name: 'Dr. Hassan Al Nouri', detail: 'Public administration & business; founder of MHR GI and IBAT.' },
  { name: 'Dr. Regina Galang-Reyes', detail: '30+ years in management and executive roles (Philippines).' },
  { name: 'Dr. Roy Prasad', detail: 'Business leadership across HR, finance and business development.' },
  { name: 'Dr. Juan Reyes, Jr.', detail: 'CEO of the Eco-Life Foundation; professorial lecturer.' },
  { name: 'Dr. Tiberiu Stoica', detail: 'Finance, econometrics, coding and machine learning.' },
  { name: 'Dr. Irena Mamonova', detail: '25+ years in academia; Director, School of Business, Belarus State University.' },
  { name: 'Dr. Khalil Aleed', detail: 'Data analysis and security; Risk Management Manager at American Express.' },
  { name: 'Dr. Ines Miranda Basaen', detail: '20+ years as a consultant and trainer (UNICEF, UNESCAP, UNDP, ILO).' },
  { name: 'Dr. Krystyna Krol', detail: '30+ years across corporate and academia; accreditation evaluator.' },
  { name: 'Dr. Natalia Blagoeva', detail: 'C-level and entrepreneurial leader; leadership coach and keynote speaker.' },
  { name: 'Fructuoso T. Sabug Jr.', detail: '20+ years as an executive; 30+ years teaching management.' },
  { name: 'Dr. Martin Nielsen', detail: '30+ years as an entrepreneur; 20+ years of university teaching.' },
  { name: 'Hemarubiny Doraisamy', detail: 'International business development; lecturer at Asia e University.' },
  { name: 'Dr. Mohammad Adam Bakar', detail: '30+ years in academia and corporate; former Vice Chancellor (Malaysia).' },
  { name: 'Dr. Lawrence H. Rubly', detail: '35+ years in academia; Director, Loop Campus, Oakton Community College.' },
  { name: 'Dr. Kinan Naddeh', detail: 'Accounting and auditing; Damascus University and Deloitte.' },
  { name: 'Dr. Joseph Riad Messiha Ghobrial Youssef', detail: 'Industrial engineering; experience across FMCG, pharma and oil & gas.' },
  { name: 'Dr. Saad Samir Saad Saaied', detail: '13+ years in economic research.' },
  { name: 'Dr. Islam Ibrahim Abdelbary', detail: 'Economist; PhD, University of Plymouth (Egypt).' },
  { name: 'Dr. Sathiswaran Uthamaputhran', detail: 'PhD in management studies; University of Aberdeen.' },
  { name: 'Dr. Saiful Amin Jalun', detail: 'Senior lecturer; strategic management coordinator.' },
  { name: 'Dr. Mageswari Ranjanthran', detail: 'Lecturer at Sunway Business School; PhD in sustainable development.' },
  { name: 'Dr. Joseph Devaraju', detail: 'Corporate trainer in finance, audit and statistics.' },
  { name: 'Dr. Khaled Alzafari', detail: 'PhD, Technische Universität Berlin; statistics and research methodology.' },
];

/** Two external assessors listed on the faculty page. */
export const EXTERNAL_ASSESSORS: readonly FacultyMember[] = [
  { name: 'Dr. Zin Ko Ko', detail: 'Founder, Myanmar Commercial College.' },
  { name: 'Dr. Nan Wai Linn', detail: 'Vice Principal, Myanmar Commercial College.' },
];

/** Regions represented across the faculty (as listed on the source page). */
export const FACULTY_REGIONS: readonly string[] = [
  'Switzerland',
  'Turkey',
  'Philippines',
  'Australia',
  'United Kingdom',
  'Belarus',
  'Malaysia',
  'United States',
  'Syria',
  'Egypt',
  'Myanmar',
  'Middle East',
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 4 · ACCREDITATIONS & MEMBERSHIPS   (source: /about/accreditations-and-memberships/)
 *     EduQua is DELIBERATELY excluded here — its mark/section stand alone.
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface Accreditation {
  name: string;
  abbr: string;
  /** Status / scope, carrying scope per the Claims Rule. */
  status: string;
  /** Path to the real logo/badge asset (public/media). */
  logo: string;
  /** Alt text for the logo, reused verbatim from the media manifest. */
  logoAlt: string;
  /** Optional extra factual line (scope/recognition already published). */
  detail?: string;
  /** Retained as a graceful fallback should the logo image fail to load. */
  Icon: LucideIcon;
}

export const ACCREDITATIONS: readonly Accreditation[] = [
  {
    name: 'Accreditation Council for Business Schools and Programs',
    abbr: 'ACBSP',
    status: 'Accredited — MBA & DBA programmes. ACBSP is recognised by CHEA in the United States.',
    logo: '/media/images/accreditation/acbsp-accredited-program-badge.jpg',
    logoAlt: 'ACBSP Accredited Program badge.',
    Icon: Award,
  },
  {
    name: 'International Swiss Accreditation Services for Adult & Higher Education',
    abbr: 'ISASAHE',
    status: 'Premier Accreditation.',
    logo: '/media/images/accreditation/isasahe-logo.png',
    logoAlt:
      'ISASAHE (International Swiss Accreditation Services for Adult and Higher Education) wordmark logo.',
    Icon: ShieldCheck,
  },
  {
    name: 'International Accreditation Council for Business Education',
    abbr: 'IACBE',
    status: 'Educational Member.',
    logo: '/media/images/accreditation/iacbe-educational-member-badge.jpg',
    logoAlt: 'IACBE Educational Member logo badge.',
    Icon: BadgeCheck,
  },
  {
    name: 'International Education Accreditation Council',
    abbr: 'IEAC',
    status: 'Accredited institution.',
    logo: '/media/images/accreditation/ieac-accredited-badge.png',
    logoAlt: 'IEAC (International Education Accreditation Council) accredited badge.',
    Icon: ClipboardCheck,
  },
  {
    name: 'Swiss Private School Register',
    abbr: 'Privatschulregister',
    status: 'Listed / active registration.',
    logo: '/media/images/accreditation/swiss-private-school-register-logo.png',
    logoAlt: 'Logo of the Swiss Private School Register (Privatschulregister Schweiz).',
    Icon: Landmark,
  },
];

/** QS rankings — each carries its year and scope (Claims Rule). */
export interface Ranking {
  figure: string;
  label: string;
}

export const RANKINGS: readonly Ranking[] = [
  { figure: '#19', label: 'QS Online MBA Rankings 2026 — Europe, Middle East & Africa' },
  { figure: '#46', label: 'QS Online MBA Rankings 2026 — Global' },
  { figure: '5★', label: 'QS 5-Stars for Business & Management Studies, 2026' },
];

/**
 * Real accreditation / ranking marks present in /public/accreditations. EduQua is
 * not among them by design. ACBSP has no general-site mark here (its logo is
 * reserved for MBA & DBA contexts), so it appears as text above, not as a badge.
 */
export interface AccreditationMark {
  src: string;
  alt: string;
}

export const ACCREDITATION_MARKS: readonly AccreditationMark[] = [
  { src: '/accreditations/isasahe.png', alt: 'ISASAHE institutional accreditation' },
  { src: '/accreditations/ieac.png', alt: 'IEAC — International Education Accreditation Council, accredited' },
  { src: '/accreditations/swiss-register.png', alt: 'Swiss Private School Register (Privatschulregister)' },
  { src: '/accreditations/qs-top-universities.png', alt: 'QS Top Universities' },
  { src: '/accreditations/qs-stars.png', alt: 'QS Stars overall rating' },
];

/**
 * Institutional-status disclaimer (mandated context). Reproduced from the live
 * site / Appendix G; US spelling is intentional and must not be "corrected".
 */
export const INSTITUTIONAL_STATUS =
  'IBAS is a private institution and does not hold recognition under the Swiss Higher Education Act (HEdA). As such, it is not accredited by the Swiss federal government or any cantonal authority.';

/* ═══════════════════════════════════════════════════════════════════════════
 * 5 · EDUQUA ★   (source: /eduqua-certification/; Appendix F logo restriction)
 *     TEXT ONLY on this page: "IBAS is EduQua Certified". NO logo, and never
 *     displayed beside the other accreditation marks.
 * ═══════════════════════════════════════════════════════════════════════════ */

/** The only approved certification wording for use off the dedicated EduQua page. */
export const EDUQUA_STATEMENT = 'IBAS is EduQua Certified.';

/** Facts about EduQua, reproduced from the live EduQua page. */
export interface EduQuaFact {
  label: string;
  value: string;
  Icon: LucideIcon;
}

export const EDUQUA_FACTS: readonly EduQuaFact[] = [
  {
    label: 'First of its kind',
    value:
      'The first Swiss quality label geared towards adult continuing education, founded in 2000.',
    Icon: Sparkles,
  },
  {
    label: 'National standard',
    value:
      'The first quality certification for public and private educational institutions in Switzerland.',
    Icon: BadgeCheck,
  },
  {
    label: 'Independent oversight',
    value:
      'Operates under the oversight of the Swiss Accreditation Bureau (SAS) and the State Secretariat for Economic Affairs (SECO).',
    Icon: ShieldCheck,
  },
  {
    label: 'Widely recognised',
    value: 'Held by more than 1,000 certified schools, institutes, and academies.',
    Icon: Landmark,
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 6 · QUALITY ASSURANCE ★   (Appendix A VERBATIM + /about/quality-assurance/)
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Verbatim opening statement from the live Quality Assurance page. */
export const QUALITY_STATEMENT =
  'At the International Business Academy of Switzerland, quality assurance is a top priority, upheld through rigorous accreditation standards, an unwavering commitment to excellence, and a dedication to fostering a dynamic learning environment.';

/** Verbatim framing of the PDCA cycle (Appendix A). */
export const PDCA_INTRO =
  'The Quality Management Cycle, Plan-Do-Check-Act (PDCA), is a cornerstone of the quality assurance strategy at IBAS. We are committed to excellence in academic programs and overall school management.';

export interface PdcaPhase {
  phase: string;
  /** Description reproduced verbatim from Appendix A. */
  body: string;
  Icon: LucideIcon;
}

export const PDCA_PHASES: readonly PdcaPhase[] = [
  {
    phase: 'Plan',
    body: 'Objectives are set, and detailed strategies are developed to enhance educational outcomes and administrative efficiency.',
    Icon: Target,
  },
  {
    phase: 'Do',
    body: 'These plans are implemented, with faculty and staff executing instructional and operational activities designed to meet predefined goals.',
    Icon: PlayCircle,
  },
  {
    phase: 'Check',
    body: 'Rigorous assessment and evaluation of the implemented actions, using both qualitative and quantitative metrics to measure success and identify areas for improvement.',
    Icon: ClipboardCheck,
  },
  {
    phase: 'Act',
    body: 'Making informed adjustments based on the evaluation results, ensuring continuous improvement.',
    Icon: RefreshCw,
  },
];

/** Verbatim closing commitment (Appendix A). */
export const PDCA_CLOSING =
  'This systematic approach allows IBAS to maintain high standards, respond proactively to challenges, and foster an environment of ongoing development and excellence.';

/* ═══════════════════════════════════════════════════════════════════════════
 * 7 · SOCIAL RESPONSIBILITY ★   (Appendix E — VERBATIM)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const SR_COMMITMENT: readonly string[] = [
  'IBAS is committed to responsible education, transparent governance, and stakeholder accountability.',
  'As an international postgraduate institution, our responsibility extends beyond academic delivery. We are accountable to our students, faculty, business partners, academic collaborators, alumni, and the broader professional community.',
  'Responsibility at IBAS is operational, measurable, and continuously reviewed.',
];

/** The three structuring principles (verbatim). */
export const SR_PRINCIPLES: readonly string[] = [
  'Responsible education aligned with contemporary global challenges',
  'Responsible governance and stakeholder engagement',
  'An inclusive, people-focused institutional culture',
];

export interface SrTheme {
  title: string;
  body: string;
  Icon: LucideIcon;
}

/** Responsible education & curriculum design — five themes (verbatim). */
export const SR_CURRICULUM_THEMES: readonly SrTheme[] = [
  {
    title: 'Corporate Responsibility',
    body: 'We integrate responsible business practices, stakeholder awareness, and sustainable thinking into case discussions and applied learning.',
    Icon: Briefcase,
  },
  {
    title: 'Ethical Leadership & Governance',
    body: 'We develop decision-making frameworks grounded in accountability, integrity, and organisational responsibility.',
    Icon: Scale,
  },
  {
    title: 'Sustainability & Responsible Management',
    body: 'We address long-term value creation, environmental awareness, and responsible resource management within contemporary business contexts.',
    Icon: Leaf,
  },
  {
    title: 'Innovation & Organisational Resilience',
    body: 'We equip students to navigate uncertainty, adapt to change, and lead responsibly in complex global environments.',
    Icon: Lightbulb,
  },
  {
    title: 'Digital Transformation',
    body: 'We examine how technology reshapes leadership, strategy, and organisational responsibility in the digital economy.',
    Icon: RefreshCw,
  },
];

/**
 * Stakeholder responsibilities (verbatim). "Expect" and "act" are kept as two
 * faithful lists rather than forced into 1:1 rows (the source pairing is not
 * strictly ordered) — no wording is changed.
 */
export interface SrStakeholder {
  group: string;
  intro: string;
  expects: readonly string[];
  acts: readonly string[];
  Icon: LucideIcon;
}

export const SR_STAKEHOLDERS: readonly SrStakeholder[] = [
  {
    group: 'Students',
    intro:
      'IBAS provides international business education designed to be academically rigorous, industry-relevant, and accessible across borders.',
    expects: [
      'High-quality business programmes aligned with current business realities',
      'Knowledgeable, competent, and internationally experienced faculty',
      'A reliable and user-friendly Learning Management System for online learners',
      'Clear communication and timely academic feedback',
      'Transparent tuition structures and financing information',
      'Career-oriented guidance, mentorship, and networking opportunities',
    ],
    acts: [
      'We regularly collect and review student feedback to strengthen courses, teaching effectiveness, and delivery methods.',
      'We maintain open communication channels and virtual and in-person open-office policies.',
      'We update curricula to reflect evolving industry needs, global economic developments, and responsible leadership practices.',
      'We provide flexible scheduling to support international learners across different time zones.',
      'For on-site students, we offer structured support including residency guidance, internship assistance and access to campus-based professional networks.',
    ],
    Icon: GraduationCap,
  },
  {
    group: 'Faculty',
    intro:
      'Our responsibility extends to creating a professional academic environment where faculty can contribute meaningfully and develop professionally.',
    expects: [
      'Clear institutional direction and transparent organisational structure',
      'Fair and non-discriminatory treatment',
      'Reliable academic and administrative support services',
      'Opportunities for continuous professional development',
    ],
    acts: [
      'We provide clearly defined policies and accessible governance processes.',
      'We collect regular faculty feedback and maintain open dialogue with academic staff.',
      'We organise Faculty Innovation Labs to explore contemporary business topics and effective teaching approaches.',
      'We encourage peer collaboration and knowledge exchange across disciplines and cultures.',
    ],
    Icon: Users,
  },
  {
    group: 'Business Community',
    intro:
      'IBAS recognises its responsibility to contribute positively to the business community by preparing graduates who are competent, ethical, and professionally accountable.',
    expects: [
      'A consistent pipeline of capable internship candidates',
      'Graduates equipped with practical skills, strategic thinking, and ethical awareness',
      'Efficient and transparent collaboration processes',
      'Long-term professional relationships',
    ],
    acts: [
      'We maintain structured internship pathways aligned with programme competencies.',
      'We collect employer feedback on student performance and workplace readiness.',
      'We facilitate company visits, keynote engagements, and participation in professional Labs and communities of practice.',
      'We engage in ongoing dialogue with industry representatives to ensure our curricula reflect contemporary business expectations.',
    ],
    Icon: Briefcase,
  },
  {
    group: 'Academic Partners',
    intro: 'IBAS maintains responsible partnerships built on clarity, alignment, and mutual accountability.',
    expects: [
      'Clear policies and academic requirements',
      'Consistent delivery of learning outcomes',
      'Professional communication and coordination',
      'Mechanisms for structured feedback and improvement',
    ],
    acts: [
      'We establish clear agreements outlining academic standards and shared responsibilities.',
      'We conduct regular coordination meetings and collect structured feedback.',
      'We harmonise delivery practices to maintain consistency across locations.',
      'We continuously review processes to safeguard academic integrity and programme quality.',
    ],
    Icon: Handshake,
  },
  {
    group: 'Alumni',
    intro:
      'Our responsibility continues beyond graduation. IBAS views alumni as long-term members of our professional and academic network.',
    expects: [
      'Networking and career development opportunities',
      'Access to continued learning and upskilling',
      'Opportunities to contribute to academic and professional dialogue',
    ],
    acts: [
      'We organise focus groups and career surveys to understand alumni progression and professional impact.',
      'We invite alumni to participate in Labs, panels, and communities of practice.',
      'We support continued engagement through executive learning initiatives and professional forums.',
    ],
    Icon: Award,
  },
];

/** Diversity & inclusive academic culture (verbatim). */
export const SR_DIVERSITY_INTRO =
  'Inclusion and diversity are integral to our social responsibility as an international business school. Across our students, faculty, leadership, and professional staff, we recognise that equitable access, fair treatment, and cross-cultural understanding are foundational to responsible education.';

export const SR_DIVERSITY_DIMENSIONS: readonly string[] = [
  'Age',
  'Gender',
  'Nationality',
  'Cultural Background',
  'Professional & Industry Experience',
];

export const SR_DIVERSITY_PILLARS: readonly SrTheme[] = [
  {
    title: 'Inclusive Access',
    body: 'We provide international business education designed to be accessible across borders, supporting learners and professionals from varied geographic, cultural, and professional contexts.',
    Icon: Accessibility,
  },
  {
    title: 'Equal Treatment & Fair Engagement',
    body: 'We uphold transparent and non-discriminatory policies in recruitment, teaching, leadership, and institutional engagement. Fair and equitable treatment across students, faculty, and staff is a core principle of our governance.',
    Icon: Scale,
  },
  {
    title: 'Diverse Perspectives in Learning and Leadership',
    body: 'By fostering a multicultural academic and professional environment, we encourage critical thinking, respectful dialogue, and exposure to varied business realities — essential foundations for responsible leadership.',
    Icon: Globe,
  },
  {
    title: 'People-Focused Institutional Culture',
    body: 'Accessibility, open communication, and mutual respect guide our daily interactions. We maintain structured support systems and clear communication channels to ensure that all members of our community are treated with fairness and dignity.',
    Icon: Users,
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 8 · CODE OF CONDUCT ★   (Appendix D — VERBATIM. Titled exactly "Code of Conduct".)
 * ═══════════════════════════════════════════════════════════════════════════ */

/** "Every Student Shall" — responsibilities (verbatim). */
export const COC_SHALL: readonly string[] = [
  'Obey the laws of his/her country and the country where he/she is studying.',
  'Obey the rules and regulations of IBAS, MGWA center and its satellite centers.',
  'Co-operate with the school authorities in all school matters, whether personal or on behalf of others, including providing information and evidence.',
  'Attend learning sessions, except with the prior permission from the subject teacher.',
  'Sit for examinations, unless barred from doing so.',
  'Be responsible for safeguarding and ensuring the safety of the property of IBAS used by him/her.',
];

/** "Every Student Shall NOT" — prohibitions (verbatim). */
export const COC_SHALL_NOT: readonly string[] = [
  'No student is permitted to be involved in any activity or activities or conduct which may damage or harm the interests, harmony, or good name of IBAS or its students, staff, officers or employees.',
  'No student is permitted to use any lecture, tutorial or teaching materials which are provided to him/her by IBAS for the purpose of publication, distribution or dissemination, whether for payment or otherwise.',
  'No student may plagiarise the intellectual property of others, including data, ideas, publications and inventions.',
  'No student may, cheat or attempt to cheat or act in a manner that can be interpreted as cheating or attempting to cheat in an examination.',
  'No student or group of students may organize, conduct or participate in any activity in the name of IBAS without permission or written instructions to do so from the center.',
  'No student or group of students may promote, manage, or assist in the collection of money or contributions in the name of IBAS without permission or written instruction to do so from IBAS.',
  'No student or group of students may make any statement to the Press in the name of IBAS without the written permission of IBAS.',
];

/** Penalties that may follow a breach of the Code (verbatim). */
export const COC_PENALTIES: readonly string[] = [
  'warning',
  'fine',
  'exclusion from any specified part or parts of the institution',
  'suspension from membership of the institution for a fixed period',
  'payment of compensation or damages for any damage to property and institution facilities or any third-party claimant',
  'nullification of examination results or any part of the examination results',
  'expulsion from the institution',
];

/** Disciplinary process — key verbatim points (investigation, proceeding, appeal). */
export const COC_DISCIPLINARY: readonly string[] = [
  'Every report of wrongdoing shall be investigated by the Investigation Committee and the Investigation Committee shall decide whether the alleged offense is serious, minor or there is no case to answer.',
  'Serious offenses include academic dishonesty, fraud, plagiarism, any activity that adversely affects the good name of IBAS and involvement in criminal activities.',
  'If the student pleads not guilty and his/her explanation is not being accepted by the Investigation Committee, the student shall be informed of his/her offense and required to appear before the Disciplinary Committee within a period not less than 14 days from the date of notification.',
  'A student who is not satisfied with the decision of the Disciplinary Committee can submit a written appeal providing the grounds of appeal to the President for consideration by the Institution Board, on condition that such an appeal is submitted within 14 days from the date of sentencing. The decision of the Board of Directors shall be final.',
];

/** Plagiarism policy (verbatim). */
export const COC_PLAGIARISM_DEFINITION =
  'Plagiarism is "thoughts theft" or the wrongful acquisition of another author\'s ideas, thoughts, language or expressions and presenting them as one\'s own ideas without giving credit to the original author.';

export interface PlagiarismLevel {
  level: string;
  body: string;
}

export const COC_PLAGIARISM_LEVELS: readonly PlagiarismLevel[] = [
  {
    level: 'Level 1 Infraction',
    body: 'If the student commits plagiarism for the first time and the plagiarized work does not exceed 50% of the paper, instructor will send informal warning to student and require the student to resubmit the work for assessment and submit an incident report form to the Dean.',
  },
  {
    level: 'Level 2 Infraction',
    body: 'If the student repeats the plagiarism and the plagiarized work exceeds 50% of the paper, the instructor will consider applying a failing grade for the work and will submit a plagiarism report to the Dean.',
  },
  {
    level: 'Level 3 Infraction',
    body: 'If the student repeats the plagiarism and the plagiarized work exceeds 50% of the paper, the instructor will apply a failing grade for the course and receive a formal warning letter.',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 9 · CONTACT ★   (Appendix B VERBATIM departmental table + /contact-us/)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const CONTACT_ADDRESS = 'Gewerbestrasse 14, 8800 Thalwil, Switzerland';
export const CONTACT_PHONE = '+41 76 623 7895';
export const CONTACT_PHONE_HOURS = 'Monday & Thursday, 09:30–17:00 (except school & public holidays)';
export const CONTACT_CAMPUS_HOURS =
  'According to the class schedule — email the campus manager for this month’s on-site opening hours.';

export interface ContactEmail {
  label: string;
  email: string;
  Icon: LucideIcon;
}

export const CONTACT_EMAILS: readonly ContactEmail[] = [
  { label: 'General & admissions', email: 'admissions@ibas.edu.eu', Icon: Mail },
  { label: 'On-site / campus', email: 'campus.manager@ibas.edu.eu', Icon: Building2 },
  { label: 'MENA region', email: 'ibas.mena@ibas.edu.eu', Icon: Globe },
];

/**
 * Departmental directory — Appendix B (verbatim support areas). A role-based
 * alias is used for the On-Site Campus Manager rather than a personal address,
 * per the compliance guidance.
 */
export interface ContactDepartment {
  role: string;
  contact: string;
  supportAreas: readonly string[];
  availability: string;
}

export const CONTACT_DEPARTMENTS: readonly ContactDepartment[] = [
  {
    role: 'Admissions',
    contact: 'admissions@ibas.edu.eu',
    supportAreas: ['Applications', 'Documents', 'Admission process'],
    availability: 'Before enrollment only.',
  },
  {
    role: 'Registrar',
    contact: 'registrar@ibas.edu.eu',
    supportAreas: [
      'Student verification / alumni',
      'Programme changes or clarifications',
      'LMS access or issues',
      'Grades and transcripts',
      'Financial requests',
    ],
    availability:
      'After enrollment. Typical response time: 1–3 business days, during regular working hours.',
  },
  {
    role: 'IT Support',
    contact: 'it-support@ibas.edu.eu',
    supportAreas: ['Technical help', 'IT-related issues'],
    availability: 'Responses provided during regular working hours.',
  },
  {
    role: 'On-Site Campus Manager',
    contact: 'campus.manager@ibas.edu.eu',
    supportAreas: [
      'General on-site inquiries',
      'Gemeinde registration',
      'Health insurance',
      'Administrative matters',
    ],
    availability: 'Available during set office hours (Mon & Thu) 09:30–17:00. Messages outside these times may be answered later.',
  },
  {
    role: 'Study Abroad Agents',
    contact: 'Contact your assigned agent',
    supportAreas: [
      'Programme changes (duration, specialisation)',
      'Documentation',
      'Communication with IBAS',
    ],
    availability:
      'Available throughout your studies; agents continue to support you for online studies and after your arrival in Switzerland if you are on-site.',
  },
];

export interface SocialLink {
  label: string;
  href: string;
  Icon: LucideIcon;
}

export const CONTACT_SOCIAL: readonly SocialLink[] = [
  { label: 'Facebook', href: 'https://facebook.com/IBAS.Education/', Icon: Users },
  { label: 'Instagram', href: 'https://instagram.com/ibas.mgwegroup', Icon: Camera },
  {
    label: 'LinkedIn',
    href: 'https://linkedin.com/school/international-business-academy-of-switzerland-gmbh',
    Icon: Briefcase,
  },
  { label: 'YouTube', href: 'https://youtube.com/channel/UC_C7uAOAH9RMzZs-CKCZ62w', Icon: Video },
];

/** Icons re-exported for section headers that want a matching glyph. */
export const CONTACT_ICONS = { MapPin, Phone, Gavel, ScrollText } as const;
